import axios from 'axios';
import { openSrchV3 } from 'conf/gconf';
import { langutil } from 'language/langutil';
import { SrchType, useZstdSrch } from 'mainpage/toparea/srchinput/useZstdSrch';
import { getDlCenterView } from 'pages/dlcenter/dlcenter-view-pref';
import { useZstdDlCenter } from 'pages/dlcenter/useZstdDlCenter';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import svcs from 'services';
import { buildNewSearchUrl, buildSearch3Url, buildSpellerQueryUrl } from 'util/srchurl';
import { isPathStartsWith } from 'util/ssqutil';
import { sanitizeSearchInput } from 'util/strutil';

type DoSrchProps = {
  input: string;
  country: string;
  lang: string;
};

type SrchHandler = (props: DoSrchProps) => void;

// 단순 경로 라우팅 테이블 (navigate만 사용하는 타입들)
const SRCH_ROUTE_TABLE: Partial<Record<SrchType, string>> = {
  [SrchType.KNOWLEDGE]: '/community/blog',
  [SrchType.QNA]: '/community/qna',
  [SrchType.NEWS_EVENT]: '/community/board',
  [SrchType.SAMPLE]: '/service/sample',
};

export const useHandleSrch = () => {
  const { srchType, input: srchInput } = useZstdSrch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { triggerSrch } = useZstdDlCenter();

  const doSrchAll = useCallback(
    ({ input, country, lang }: DoSrchProps) => {
      const isSearch3 = isPathStartsWith(pathname, '/search3');
      if (isSearch3 || openSrchV3) {
        window.open(buildSearch3Url({ country, lang, input }), '_self');
        return;
      }

      axios.get(buildSpellerQueryUrl(input)).then((spellCheck) => {
        const url = buildNewSearchUrl({
          country,
          lang,
          input,
          speller: spellCheck.data.speller || undefined,
        });
        window.open(url, '_self');
      });
    },
    [pathname]
  );

  const doSrchDlcenter = useCallback(
    ({ input, country, lang }: DoSrchProps) => {
      const isClassicView = getDlCenterView() === 'classic';

      if (isClassicView) {
        const isClassicPage = isPathStartsWith(pathname, '/product/category/');
        if (isClassicPage && srchInput === input) {
          // 이미 classic 페이지에 있고 같은 검색어면 재검색 불필요
          return;
        }
        const categoryId = isClassicPage
          ? (pathname.match(/\/product\/category\/(\d+)/)?.[1] ?? '0')
          : '0';
        navigate(`/${country}/${lang}/product/category/${categoryId}?searchInput=${encodeURIComponent(sanitizeSearchInput(input))}`);
        return;
      }

      const isDlCenterPage = pathname.includes('/dlcenter');
      if (isDlCenterPage && srchInput === input) {
        triggerSrch();
        return;
      }
      navigate(`/${country}/${lang}/dlcenter?searchInput=${encodeURIComponent(sanitizeSearchInput(input))}`);
    },
    [navigate, srchInput, triggerSrch, pathname]
  );

  // 라우팅 테이블 기반 핸들러 생성
  const createRouteHandler = useCallback(
    (path: string): SrchHandler =>
      ({ input, country, lang }) => {
        navigate(`/${country}/${lang}${path}?searchInput=${encodeURIComponent(sanitizeSearchInput(input))}`);
      },
    [navigate]
  );

  // 검색 타입별 핸들러 맵
  const srchHandlers = useMemo((): Partial<Record<SrchType, SrchHandler>> => {
    const handlers: Partial<Record<SrchType, SrchHandler>> = {
      [SrchType.ALL]: doSrchAll,
      [SrchType.DLCENTER]: doSrchDlcenter,
    };

    // 라우팅 테이블의 경로를 핸들러로 변환
    for (const [type, path] of Object.entries(SRCH_ROUTE_TABLE)) {
      handlers[type as SrchType] = createRouteHandler(path);
    }

    return handlers;
  }, [doSrchAll, doSrchDlcenter, createRouteHandler]);

  // 검색어 없이 게시판 변경 시 해당 게시판으로 이동
  const handleNavToBoard = useCallback(
    (type: SrchType) => {
      const { country, lang } = langutil.splitLangCode(svcs.svcLang.lang());
      if (type === SrchType.ALL) {
        if (openSrchV3) {
          window.open(buildSearch3Url({ country, lang, input: '' }), '_self');
          return;
        }
        window.open(buildNewSearchUrl({ country, lang, input: '' }), '_self');
        return;
      }
      const dlcenterPath = getDlCenterView() === 'classic' ? '/product/category/0' : '/dlcenter';
      const path = SRCH_ROUTE_TABLE[type] ?? (type === SrchType.DLCENTER ? dlcenterPath : null);
      if (path) {
        navigate(`/${country}/${lang}${path}`);
      }
    },
    [navigate]
  );

  const handleSrch = useCallback(
    (_input: string) => {
      const { country, lang } = langutil.splitLangCode(svcs.svcLang.lang());
      const input = sanitizeSearchInput(_input);
      if (!input) return;

      window.scrollTo(0, 0);
      // 테이블 기반 라우팅: 해당 타입의 핸들러가 없으면 기본(ALL) 핸들러 사용
      const handler = srchHandlers[srchType] || doSrchAll;
      handler({ input, country, lang });
    },
    [srchType, srchHandlers, doSrchAll]
  );

  return { handleSrch, handleNavToBoard };
};
